import { useState, useEffect } from 'react';
import { portalListMeters, portalListMeterReadings, portalSubmitMeterReading } from '../services/api';
import {
  Gauge, Plus, X, Lightning, Drop, Fire, Flame, Wrench, Warning, CheckCircle, ClockClockwise,
} from '@phosphor-icons/react';

const METER_TYPE_LABELS = {
  strom: { label: 'Strom', icon: Lightning, color: 'amber' },
  wasser_kalt: { label: 'Wasser kalt', icon: Drop, color: 'blue' },
  wasser_warm: { label: 'Wasser warm', icon: Drop, color: 'orange' },
  heizung: { label: 'Heizung', icon: Fire, color: 'red' },
  gas: { label: 'Gas', icon: Flame, color: 'yellow' },
  custom: { label: 'Sonstiger', icon: Wrench, color: 'slate' },
};

const REASON_LABELS = {
  regular: 'Regulär',
  einzug: 'Einzug',
  auszug: 'Auszug',
  jahresende: 'Jahresende',
};

const STATUS_BADGES = {
  submitted: { label: 'Eingegangen', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  verified: { label: 'Geprüft', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  rejected: { label: 'Abgelehnt', cls: 'bg-red-100 text-red-800 border-red-200' },
};

export default function TenantZaehler() {
  const [meters, setMeters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMeter, setOpenMeter] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await portalListMeters();
      setMeters(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-900"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="tenant-zaehler-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-slate-900">Zählerstände</h1>
        <p className="text-slate-500 mt-1 text-sm">
          Trage deine Zählerstände regelmäßig ein. Für die Jahresabrechnung sind Werte zum Jahresende bzw. bei Ein- und Auszug besonders wichtig.
        </p>
      </div>

      {meters.length === 0 ? (
        <div className="bg-white border border-slate-200 p-10 text-center rounded-lg">
          <Gauge weight="light" className="w-14 h-14 text-slate-300 mx-auto mb-3" />
          <h3 className="font-heading text-lg font-bold text-slate-900 mb-1">Keine Zähler hinterlegt</h3>
          <p className="text-slate-500 text-sm">Sobald dein Vermieter Zähler für deine Wohnung anlegt, erscheinen sie hier.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {meters.map(m => {
            const meta = METER_TYPE_LABELS[m.meter_type] || METER_TYPE_LABELS.custom;
            const Icon = meta.icon;
            return (
              <div key={m.id} className="bg-white border border-slate-200 rounded-lg p-5 hover:border-emerald-400 transition-colors" data-testid={`meter-card-${m.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-12 h-12 rounded-lg bg-${meta.color}-100 flex items-center justify-center flex-shrink-0`}>
                    <Icon weight="bold" className={`w-6 h-6 text-${meta.color}-700`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-lg font-bold text-slate-900 truncate">{m.label}</h3>
                    <p className="text-xs text-slate-500">{meta.label} · {m.unit_of_measure} {m.serial_number && `· Nr. ${m.serial_number}`}</p>
                  </div>
                </div>
                <div className="mt-4 border-t border-slate-100 pt-4">
                  {m.last_reading ? (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">Letzter Stand</p>
                      <p className="font-mono text-2xl font-black text-slate-900 mt-1">{m.last_reading.value} <span className="text-base font-normal text-slate-500">{m.unit_of_measure}</span></p>
                      <p className="text-xs text-slate-500 mt-0.5">am {m.last_reading.reading_date}</p>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 italic">Noch keine Ablesung erfasst</p>
                  )}
                </div>
                <button
                  onClick={() => setOpenMeter(m)}
                  className="mt-4 w-full bg-emerald-700 hover:bg-emerald-800 text-white py-2.5 rounded font-medium inline-flex items-center justify-center gap-2"
                  data-testid={`submit-reading-${m.id}`}
                >
                  <Plus weight="bold" className="w-4 h-4" /> Ablesung erfassen
                </button>
              </div>
            );
          })}
        </div>
      )}

      {openMeter && (
        <TenantReadingModal
          meter={openMeter}
          onClose={() => setOpenMeter(null)}
          onSaved={() => { load(); }}
        />
      )}
    </div>
  );
}

function TenantReadingModal({ meter, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ reading_date: today, value: '', reason: 'regular', notes: '' });
  const [photo, setPhoto] = useState(null); // {data, mime}
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [warnings, setWarnings] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await portalListMeterReadings(meter.id);
        setHistory(r.data);
      } catch (e) { console.error(e); }
    })();
  }, [meter.id]);

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) { setPhoto(null); return; }
    if (file.size > 5 * 1024 * 1024) {
      alert('Foto darf maximal 5 MB groß sein.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const base64 = (dataUrl || '').toString().split(',')[1];
      setPhoto({ data: base64, mime: file.type || 'image/jpeg' });
    };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.value === '' || form.value === null) { alert('Bitte Wert eingeben'); return; }
    setSaving(true);
    setWarnings(null);
    try {
      const res = await portalSubmitMeterReading(meter.id, {
        reading_date: form.reading_date,
        value: parseFloat(String(form.value).replace(',', '.')),
        reason: form.reason,
        notes: form.notes || null,
        photo_data_base64: photo?.data,
        photo_mime: photo?.mime,
      });
      const w = res.data?.warnings || [];
      setWarnings(w);
      setShowSuccess(true);
      // refresh history
      const r = await portalListMeterReadings(meter.id);
      setHistory(r.data);
      setForm({ reading_date: today, value: '', reason: 'regular', notes: '' });
      setPhoto(null);
      if (onSaved) onSaved();
      setTimeout(() => setShowSuccess(false), 5000);
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.detail || err.message));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="tenant-reading-modal">
      <div className="bg-white w-full max-w-lg rounded-lg overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-emerald-900 text-white">
          <div>
            <h2 className="font-heading text-xl font-bold">{meter.label}</h2>
            <p className="text-xs text-emerald-100">{meter.unit_of_measure} {meter.serial_number && ` · Nr. ${meter.serial_number}`}</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X weight="bold" className="w-5 h-5" /></button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4 overflow-y-auto">
          {showSuccess && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm text-emerald-800 flex items-start gap-2">
              <CheckCircle weight="bold" className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold">Ablesung gespeichert!</p>
                {warnings?.length > 0 ? (
                  <ul className="mt-1 text-xs list-disc list-inside text-amber-700">
                    {warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                ) : (
                  <p className="text-xs text-emerald-700">Wir leiten den Wert an deinen Vermieter weiter.</p>
                )}
              </div>
            </div>
          )}

          {meter.last_reading && (
            <div className="bg-slate-50 rounded p-3 text-sm">
              <p className="text-slate-500 text-xs uppercase tracking-wider font-bold">Letzter Stand</p>
              <p className="font-mono font-bold text-slate-900">{meter.last_reading.value} {meter.unit_of_measure} <span className="text-xs text-slate-500 font-normal">am {meter.last_reading.reading_date}</span></p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-1">Datum</label>
              <input type="date" value={form.reading_date} max={today} onChange={e => setForm({ ...form, reading_date: e.target.value })} required className="w-full border-b border-slate-300 bg-transparent py-2 outline-none" data-testid="tenant-reading-date" />
            </div>
            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-1">Anlass</label>
              <select value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} className="w-full border-b border-slate-300 bg-transparent py-2 outline-none" data-testid="tenant-reading-reason">
                {Object.entries(REASON_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-1">Zählerstand ({meter.unit_of_measure})</label>
            <input type="text" inputMode="decimal" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} required placeholder="z.B. 12345,6" className="w-full border-b-2 border-slate-300 bg-transparent py-2 outline-none font-mono text-2xl focus:border-emerald-700" data-testid="tenant-reading-value" autoFocus />
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-1">Foto vom Zähler (optional)</label>
            <input type="file" accept="image/*" capture="environment" onChange={handlePhoto} className="text-sm text-slate-700" data-testid="tenant-reading-photo" />
            {photo && <p className="text-xs text-emerald-700 mt-1">Foto ausgewählt ({Math.round(photo.data.length * 0.75 / 1024)} KB)</p>}
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-1">Notiz (optional)</label>
            <input type="text" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full border-b border-slate-300 bg-transparent py-2 outline-none" />
          </div>
          <button type="submit" disabled={saving} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white py-3 rounded font-medium disabled:opacity-50" data-testid="tenant-save-reading">
            {saving ? 'Wird gespeichert…' : 'Ablesung speichern'}
          </button>

          {history.length > 0 && (
            <div className="border-t border-slate-200 pt-4 mt-2">
              <h3 className="font-heading text-sm font-bold text-slate-900 mb-2 inline-flex items-center gap-2"><ClockClockwise weight="bold" className="w-4 h-4" /> Bisherige Ablesungen</h3>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {history.map(r => {
                  const sb = STATUS_BADGES[r.status] || STATUS_BADGES.submitted;
                  return (
                    <div key={r.id} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0">
                      <div>
                        <p className="text-slate-900">{r.reading_date} <span className="text-xs text-slate-500">({REASON_LABELS[r.reason] || r.reason})</span></p>
                        {r.warnings?.length > 0 && (
                          <p className="text-xs text-amber-700 inline-flex items-center gap-1"><Warning weight="bold" className="w-3 h-3" /> {r.warnings[0]}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-mono font-bold text-slate-900">{r.value} {meter.unit_of_measure}</p>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${sb.cls}`}>{sb.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
